
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useUserProfiles } from "./useUserProfiles";

interface ReviewFormData {
  rating: number;
  comment?: string;
}

export interface ReviewData {
  id: string;
  rating: number;
  comment?: string;
  created_at: string;
  reviewer_id: string;
  provider_id: string;
  service_id: string;
  is_verified: boolean;
}

export interface ReviewWithUserData extends ReviewData {
  reviewer: {
    id: string;
    name: string;
    avatarInitials: string;
  };
  serviceTitle?: string;
}

interface RatingDistribution {
  star1: number;
  star2: number;
  star3: number;
  star4: number;
  star5: number;
}

interface ProviderRatingData {
  average: number;
  total: number;
  distribution: RatingDistribution;
}

interface UseReviewsReturn {
  submitting: boolean;
  loading: boolean;
  submitReview: (serviceId: string, data: ReviewFormData) => Promise<boolean>;
  getProviderReviews: (providerId: string) => Promise<ReviewWithUserData[]>;
  getProviderAverageRating: (providerId: string) => Promise<ProviderRatingData>;
  hasUserReviewedService: (serviceId: string) => Promise<boolean>;
}

export function useReviews(): UseReviewsReturn {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const { getUserProfile } = useUserProfiles();

  const submitReview = async (serviceId: string, data: ReviewFormData): Promise<boolean> => {
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("You must be logged in to submit a review");
        return false;
      }

      // Call our edge function using the proper invoke method
      const { data: result, error } = await supabase.functions.invoke('submit-review', {
        body: {
          service_id: serviceId,
          rating: data.rating,
          comment: data.comment
        }
      });

      if (error) {
        toast.error(error.message || "Failed to submit review");
        console.error("Review submission failed:", error);
        return false;
      }

      toast.success("Review submitted successfully");
      return true;
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("An unexpected error occurred while submitting your review");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const getProviderReviews = useCallback(async (providerId: string): Promise<ReviewWithUserData[]> => {
    setLoading(true);
    try {
      // Use secure function to fetch provider reviews (bypasses RLS for public viewing)
      const { data: reviews, error } = await supabase
        .rpc('get_provider_reviews', { target_provider_id: providerId });

      if (error) {
        console.error("Error fetching provider reviews:", error);
        toast.error("Failed to load reviews");
        return [];
      }

      if (!reviews || reviews.length === 0) {
        return [];
      }

      // Process the reviews - data already includes reviewer info from the secure function
      const reviewsWithUserData: ReviewWithUserData[] = [];
      
      for (const review of reviews) {
        try {
          // Get service title separately since it's not in the secure function response
          const { data: service, error: serviceError } = await supabase
            .from('services')
            .select('title')
            .eq('id', review.service_id)
            .single();
            
          reviewsWithUserData.push({
            id: review.id,
            rating: review.rating,
            comment: review.comment,
            created_at: review.created_at,
            provider_id: review.provider_id,
            reviewer_id: '', // Not provided by secure function for privacy
            service_id: review.service_id,
            is_verified: review.is_verified,
            reviewer: {
              id: '',
              name: review.reviewer_first_name || 'Anonymous',
              avatarInitials: (review.reviewer_first_name?.[0] || 'A').toUpperCase() + 'U'
            },
            serviceTitle: service?.title || 'Unknown Service'
          });
        } catch (error) {
          console.error("Error processing review data:", error);
        }
      }

      return reviewsWithUserData;
    } catch (error) {
      console.error("Error in getProviderReviews:", error);
      toast.error("Failed to load review data");
      return [];
    } finally {
      setLoading(false);
    }
  }, [getUserProfile]);

  const getProviderAverageRating = useCallback(async (providerId: string): Promise<ProviderRatingData> => {
    setLoading(true);
    try {
      // Use secure function to fetch provider reviews for rating calculation
      const { data: reviews, error } = await supabase
        .rpc('get_provider_reviews', { target_provider_id: providerId });

      if (error) {
        console.error("Error fetching provider ratings:", error);
        toast.error("Failed to load rating data");
        return { 
          average: 0, 
          total: 0, 
          distribution: { star1: 0, star2: 0, star3: 0, star4: 0, star5: 0 } 
        };
      }

      if (!reviews || reviews.length === 0) {
        return { 
          average: 0, 
          total: 0, 
          distribution: { star1: 0, star2: 0, star3: 0, star4: 0, star5: 0 } 
        };
      }

      // Calculate average rating
      const total = reviews.length;
      const sum = reviews.reduce((acc, review) => acc + Number(review.rating), 0);
      const average = sum / total;

      // Calculate distribution with explicit structure
      const distribution: RatingDistribution = {
        star1: reviews.filter(r => r.rating === 1).length,
        star2: reviews.filter(r => r.rating === 2).length,
        star3: reviews.filter(r => r.rating === 3).length,
        star4: reviews.filter(r => r.rating === 4).length,
        star5: reviews.filter(r => r.rating === 5).length,
      };

      return {
        average,
        total,
        distribution
      };
    } catch (error) {
      console.error("Error calculating provider average rating:", error);
      toast.error("Failed to calculate rating data");
      return { 
        average: 0, 
        total: 0, 
        distribution: { star1: 0, star2: 0, star3: 0, star4: 0, star5: 0 } 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const hasUserReviewedService = useCallback(async (serviceId: string): Promise<boolean> => {
    try {
      // Get the current user's session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return false; // Not logged in, can't have reviewed
      }
      
      // Check if the user has already reviewed this service
      const { data, error } = await supabase
        .from('reviews')
        .select('id')
        .eq('service_id', serviceId)
        .eq('reviewer_id', session.user.id)
        .maybeSingle();
        
      if (error) {
        console.error("Error checking for user review:", error);
        return false;
      }
      
      return !!data; // Return true if review found, false otherwise
    } catch (error) {
      console.error("Error checking if user reviewed service:", error);
      return false;
    }
  }, []);

  return {
    submitting,
    loading,
    submitReview,
    getProviderReviews,
    getProviderAverageRating,
    hasUserReviewedService
  };
}
